import { memo, useMemo } from 'react';

import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import type { CrmObject } from '@/types/crm';

import type { AssociationType } from '../../core/SchemaClient';
import { getCardUpdatedAt } from '../utils/card-updated-at';
import type { PipelineCardConfig } from '../utils/cardConfig';
import { CardRowView } from './CardSlotViews';
import { InlineCardTitle } from './InlineCardTitle';
import { CardFooter, getEntityName } from './PipelineCardParts';

// ---------------------------------------------------------------------------
// ConfigurableCardContent — unified card renderer (title + rows + footer)
// ---------------------------------------------------------------------------

export const ConfigurableCardContent = memo(function ConfigurableCardContent({
  object,
  config,
  propertySchemas,
  associatedObjects,
  associationLabels,
  owner,
  ownerAssociationTypeName,
  ownerAssociationType,
  isComposedName = false,
}: {
  object: CrmObject;
  config: PipelineCardConfig;
  propertySchemas: Record<string, PropertyTypeSchema>;
  associatedObjects: Record<string, CrmObject>;
  /** Maps association_type_name → display label for showLabel rendering */
  associationLabels: Map<string, string>;
  owner?: CrmObject;
  /** Owner association type name — enables CardOwnerPicker in the footer. */
  ownerAssociationTypeName?: string;
  /** Schema for the owner association — drives the picker's permission gate. */
  ownerAssociationType?: AssociationType;
  /** True when this entity type's display name is composed from separate
   *  fields (e.g. ahoy_first_name/ahoy_last_name) — forces the title
   *  to read-only inside InlineCardTitle. Defaults to false (direct
   *  ahoy_name — editable when permission and schema allow). */
  isComposedName?: boolean;
}) {
  const name = getEntityName(object, 'Untitled');

  const updatedAt = useMemo(
    () => getCardUpdatedAt(object.properties),
    [object.properties]
  );

  // Stable per-row keys: the slot-name signature plus the row's position in the
  // (static) config, so rows with identical slot-name combinations don't collide.
  const keyedRows = useMemo(
    () =>
      config.rows.map((row, index) => ({
        key: `${row.slots.map(s => s.name).join('|') || 'empty-row'}#${index}`,
        row,
      })),
    [config.rows]
  );

  return (
    <div>
      <InlineCardTitle
        object={object}
        title={name}
        nameSchema={propertySchemas['ahoy_name']}
        isComposedName={isComposedName}
      />
      {keyedRows.map(({ key, row }) => (
        <CardRowView
          key={key}
          row={row}
          object={object}
          propertySchemas={propertySchemas}
          associatedObjects={associatedObjects}
          associationLabels={associationLabels}
        />
      ))}
      <CardFooter
        owner={owner}
        updatedAt={updatedAt}
        item={object}
        ownerAssociationTypeName={ownerAssociationTypeName}
        ownerAssociationType={ownerAssociationType}
      />
    </div>
  );
});
